import { css } from 'aphrodite';
import { Button } from '../../../web/components-v2/Button';
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from '../../../web/components-v2/Collapsible';
import { DateInput } from '../../../web/components-v2/DateInput';
import { Input } from '../../../web/components-v2/Input';
import { IconChevronDown } from '../../focused-view/ui/icons';
import { EMPTY_REP_CARD_FILTERS, type RepCardFilters } from './repCardFilters';
import { summarizeRepCardFilters } from './repCardFilterSummary';
import { RUBRIC_SCORE_CONTROLS_ENABLED } from './rubricScoreControls';
import { styleSheet } from './repCards.styles';

interface IProps {
	filters: RepCardFilters;
	onChange: (filters: RepCardFilters) => void;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	repName: string;
}

export function RepCardFilterBar({ filters, onChange, open, onOpenChange, repName }: IProps) {
	const set = (patch: Partial<RepCardFilters>) => onChange({ ...filters, ...patch });
	const summary = summarizeRepCardFilters(filters);

	return (
		<Collapsible open={open} onOpenChange={onOpenChange}>
			<div className={css(styleSheet.filterTriggerRow)}>
				<CollapsibleTrigger
					className={css(styleSheet.filterTrigger)}
					aria-label={`${open ? 'Hide' : 'Show'} filters for ${repName}`}
				>
					<span className={css(styleSheet.filterTriggerLabel)}>Filters</span>
					{summary.length > 0 ? (
						summary.map(chip => (
							<span key={chip} className={css(styleSheet.filterSummaryChip)}>
								{chip}
							</span>
						))
					) : (
						<span className={css(styleSheet.filterTriggerHint)}>All calls</span>
					)}
					<span className={css(styleSheet.filterChevron, open ? styleSheet.filterChevronOpen : null)}>
						<IconChevronDown size={14} />
					</span>
				</CollapsibleTrigger>
				{summary.length > 0 ? (
					<Button
						variant='ghost'
						size='sm'
						styles={[styleSheet.filterClearButton]}
						onClick={() => onChange(EMPTY_REP_CARD_FILTERS)}
						aria-label={`Clear filters for ${repName}`}
					>
						Clear
					</Button>
				) : null}
			</div>
			<CollapsiblePanel>
				<div className={css(styleSheet.filters)}>
					{RUBRIC_SCORE_CONTROLS_ENABLED ? (
						<div className={css(styleSheet.filterRow)}>
							<span className={css(styleSheet.filterLabel)}>Score</span>
							<Input
								type='number'
								min={0}
								max={100}
								inputMode='numeric'
								placeholder='Min'
								aria-label='Minimum adherence score'
								value={filters.minScore}
								onChange={e => set({ minScore: e.target.value })}
								styles={[styleSheet.filterRangeInput]}
							/>
							<span className={css(styleSheet.filterRangeSep)} aria-hidden='true'>
								–
							</span>
							<Input
								type='number'
								min={0}
								max={100}
								inputMode='numeric'
								placeholder='Max'
								aria-label='Maximum adherence score'
								value={filters.maxScore}
								onChange={e => set({ maxScore: e.target.value })}
								styles={[styleSheet.filterRangeInput]}
							/>
						</div>
					) : null}
					<div className={css(styleSheet.filterRow)}>
						<span className={css(styleSheet.filterLabel)}>Length</span>
						<Input
							type='number'
							min={0}
							inputMode='numeric'
							placeholder='Min'
							aria-label='Minimum call length in minutes'
							value={filters.minMinutes}
							onChange={e => set({ minMinutes: e.target.value })}
							styles={[styleSheet.filterRangeInput]}
						/>
						<span className={css(styleSheet.filterRangeSep)} aria-hidden='true'>
							–
						</span>
						<Input
							type='number'
							min={0}
							inputMode='numeric'
							placeholder='Max'
							aria-label='Maximum call length in minutes'
							value={filters.maxMinutes}
							onChange={e => set({ maxMinutes: e.target.value })}
							styles={[styleSheet.filterRangeInput]}
						/>
						<span className={css(styleSheet.filterRangeSep)} aria-hidden='true'>
							min
						</span>
					</div>
					<div className={css(styleSheet.filterRow)}>
						<span className={css(styleSheet.filterLabel)}>Date</span>
						<DateInput value={filters.startDate} onChange={value => set({ startDate: value })} />
						<DateInput value={filters.endDate} onChange={value => set({ endDate: value })} />
					</div>
				</div>
			</CollapsiblePanel>
		</Collapsible>
	);
}
